/**
 * THE PROTECTED CAPABILITIES — plan-029 PC-DOM (design §40.8.1, §40.8.3, §40.20.1; ADR 0082, proposed; glossary §6.44
 * *Protected capability*; D-7, DD29-4, DD29-5).
 *
 * What caller confirmation can protect is a closed list, declared per agent_template as compiled catalogue data
 * (`CataloguedAgentTemplate.protectedCapability`, required on every row — `../template/agent-template-catalogue.ts`),
 * never a column and never free text (a prompt cannot enforce a "topic"):
 *
 * ```
 * capability             the tools it covers
 * bi_query               the BI query tool (`BI_QUERY_TOOL_NAME`, snapshot and live)
 * caller_recall          `recall_caller_details` (offered only while the recall is protected, RT-TOOL2)
 * connection_operation   every operation of the customer's own connections (`config.connections`, through
 *                        `decideOperation`) — never a template-provided operation (`update_order`, the payment tools)
 * ```
 *
 * The host says which family a tool line belongs to (`ToolFamily`); the BI tool and the recall tool are also known here by
 * name, so a host that names a BI or recall call `other` by mistake is still gated (fail closed). Tools in
 * {@link NEVER_PROTECTED_TOOL} are never refused, whatever is configured — **but the family is consulted first**
 * (SEC-2 F4, 2026-10-29): a customer's own connection operation (or a BI or recall call) that happens to carry a platform
 * tool's name (`end_conversation`, `take_message`, …) is that capability, never exempted by its name. Pure: no I/O.
 */
import { BI_QUERY_TOOL_NAME } from "../bi/schema.js";
/** The closed list of protectable capabilities (`party_confirmation_requirement.protected_capability`, 0188's CHECK). */
export const PROTECTED_CAPABILITY = ["bi_query", "caller_recall", "connection_operation"];
export function isProtectedCapability(value) {
    return PROTECTED_CAPABILITY.includes(value);
}
/** The platform tool that sends a confirmation code (no arguments; never protected; RT-TOOL2 defines it). */
export const REQUEST_CONFIRMATION_CODE_TOOL_NAME = "request_confirmation_code";
/** The delivery VoiceAgent's gated recall of a known caller's facts (no arguments; protected as `caller_recall`). */
export const RECALL_CALLER_DETAILS_TOOL_NAME = "recall_caller_details";
/**
 * The tools the gate never refuses (design §40.8.1): asking for a code, taking a message, ending the conversation,
 * passing to another VoiceAgent and back (a target guards itself, §40.8.5), handing off to a person and noting an
 * unanswered request — plus `wait_for_caller`, the listening tool that takes no action and reveals nothing (added by
 * PC-DOM 2026-10-29: refusing it to an unconfirmed party of a "number + SMS code" VoiceAgent would answer a pause with
 * `confirmation_required`). Names as the runtime's tool definitions spell them.
 */
export const NEVER_PROTECTED_TOOL = Object.freeze([
    REQUEST_CONFIRMATION_CODE_TOOL_NAME,
    "take_message",
    "end_conversation",
    "pass_to_voice_agent",
    "pass_back",
    "hand_off_to_person",
    "note_unanswered_request",
    "wait_for_caller",
]);
/**
 * Every tool name the platform itself gives a VoiceAgent in a conversation (SEC-2 F4, 2026-10-29): the never-protected
 * tools, the BI and recall tools, and the agent_templates' own operations (`update_order`, `customer_order_payment`, the
 * survey's `classify`). A customer's own connection operation may never carry one — the factory refuses such a
 * specification (`apps/factory/src/connection/spec.ts`) — so no name can be both a platform tool and a customer's
 * operation. The runtime's suite pins every tool name it defines to this list.
 */
export const PLATFORM_TOOL_NAME = Object.freeze([
    ...NEVER_PROTECTED_TOOL,
    BI_QUERY_TOOL_NAME,
    RECALL_CALLER_DETAILS_TOOL_NAME,
    "update_order",
    "customer_order_payment",
    "classify",
]);
/**
 * Which family a host's tool line belongs to, as the host classifies it before any family handler sees it
 * (design §40.8.4): the BI tool, the recall tool, an operation of the customer's own connections, or anything else
 * (a template-provided operation, a platform tool, the order and payment tools).
 */
export const TOOL_FAMILY = ["bi_query", "caller_recall", "connection_operation", "other"];
/**
 * The capability a tool line exercises, or `null` when it exercises none. The tool's name decides for the BI tool and the
 * recall tool whatever family the host named, so a misclassified line is still gated; the family decides before any
 * never-protected name (SEC-2 F4), so only a platform tool (family `other`) of a never-protected name is `null`.
 */
export function capabilityOfTool(toolName, family) {
    if (toolName === BI_QUERY_TOOL_NAME || family === "bi_query")
        return "bi_query";
    if (toolName === RECALL_CALLER_DETAILS_TOOL_NAME || family === "caller_recall")
        return "caller_recall";
    if (family === "connection_operation")
        return "connection_operation";
    return null;
}
/**
 * Whether a tool line is never refused: a never-protected name in the platform family only (SEC-2 F4). A call of
 * another family is never exempted by its name.
 */
export function isNeverProtected(toolName, family) {
    return capabilityOfTool(toolName, family) === null && NEVER_PROTECTED_TOOL.includes(toolName);
}
//# sourceMappingURL=capability.js.map